import React, { useEffect, useRef, useState } from "react";
import {Btn, CheckoutTileDetail, FormInput, FormTextarea, VatAllocationsTable} from "../components";
import { useCore, useLogin } from "../hooks";
import { useNavigate, useParams } from "react-router";
import {Checkout, ExtraOperation} from "depoto-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useGQL } from "../utils/GQLContext.ts";
import {formatDate, formatPrice, getDateStringFromUTCString, Schema} from "../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const CheckoutClose: React.FC = () => {
  const params = useParams()
  const gql = useGQL()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { core, checkouts, setCheckouts, setSelectedCheckoutId, currency } = useCore()
  const { user } = useLogin()
  const [checkout, setCheckout] = useState<Checkout>(null)
  // todo disabled if hasnt role for extraOp. if does and sums differ, create extraOp with note - from closing..
  const [realClosingAmount, setRealClosingAmount] = useState<number>(0)
  const [note, setNote] = useState<string>('')
  const [enumeration, setEnumeration] = useState<any>({
    1: 0,
    2: 0,
    5: 0,
    10: 0,
    20: 0,
    50: 0,
    100: 0,
    200: 0,
    500: 0,
    1000: 0,
    2000: 0,
    5000: 0,
  })
  const [enumerationSum, setEnumerationSum] = useState<number>(0)
  const [isClosing, setIsClosing] = useState<boolean>(false)
  const isClosingRef = useRef<boolean>(false) // state is not updated synchronously, ref guards fast double click
  const hasRoleExtraOperation = core.services.user.hasOneOfTheRoles(['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_CHECKOUT_EXTRA_OPERATION_CREATE'])

  useEffect(() => {
    if (params?.id) {
      refreshCheckoutsAndLoadData(params.id)
    }
  }, [])

  const refreshCheckoutsAndLoadData = async (checkoutId) => {
    const refreshedCheckouts = await core.services.checkout.getList({ filters: {} }, Schema.checkout.detailWithSums).then(r => r.items || [])
    const refreshedUser = await core.services.user.getMyProfile(Schema.user.detail)
    let filteredCheckouts = [...refreshedCheckouts].filter(c => c.enabled)
    if (refreshedUser?.checkouts?.length) {
      const cIds = refreshedUser.checkouts.map(c => c.id)
      filteredCheckouts = refreshedCheckouts.filter(c => cIds.includes(c.id))
    }
    setCheckouts(filteredCheckouts)
    const c = refreshedCheckouts.find(c => c.id === Number(checkoutId))
    if (!c) return
    const isAlreadyClosed = !c.currentClosing?.dateOpen || !!c.currentClosing?.dateClosed
    if (isAlreadyClosed) {
      navigate('/checkout-select')
      return
    }
    setCheckout(c)
    setRealClosingAmount(Number(c?.amount?.toFixed(2) || 0))
  }

  const { mutate: updateCheckoutClosing } = useMutation({
    mutationKey: ['updateCheckoutClosing'],
    mutationFn: () =>
      // mutationFn: (data: ValueTypes['updateCheckoutClosing'][]) =>
      toast.promise(
        gql('mutation')({
          updateCheckoutClosing: [
            {
              id: checkout.currentClosing.id,
              checkout: checkout.id,
              dateClosed: getDateStringFromUTCString(),
              amountClosed: checkout.amount.toFixed(2),
              amountRealClosed: realClosingAmount.toFixed(2),
              userClosed: user.id,
              noteClosed: note,
              enumeration: JSON.stringify(enumeration),
            },
            {
              errors: true,
            },
          ],
        }),
        {
          pending: 'Uzavírám pokladnu...',
          error: 'Něco se pokazilo',
          success: 'Pokladna uzavřena',
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checkouts', 'products'] })
      setSelectedCheckoutId(undefined)
      navigate('/dashboard')
    },
    onError: () => {
      isClosingRef.current = false
      setIsClosing(false)
    },
  })

  // const backToCheckoutSelect = () => {
  //   navigate('/checkout-select')
  // }

  const updateEnumeration = (key: number, val: number) => {
    const nextEnum = {...enumeration}
    nextEnum[key] = val
    setEnumeration(nextEnum)
    let sum = 0
    for (const key in nextEnum) {
      sum += Number(key) * nextEnum[key]
    }
    setEnumerationSum(sum)
  }

  const createExtraOperation = async (amount: number) => {
    const extraOp = new ExtraOperation({
      checkout,
      amount: amount.toFixed(2),
      note: `Vytvořeno při uzavírání pokladny ${checkout.name}, ${formatDate(new Date(), false, true)}`,
    })
    // @ts-ignore
    await core.services.checkout.createExtraOperation(extraOp)
    toast.success('Vyrovnávací operace byla vytvořena')
  }

  const unlockClosing = () => {
    isClosingRef.current = false
    setIsClosing(false)
  }

  const closeCheckout = async () => {
    if (isClosingRef.current) return
    isClosingRef.current = true
    setIsClosing(true)
    // checkout could have been closed on another device since this page was loaded
    let c: Checkout
    try {
      c = await core.services.checkout.getById(checkout.id, Schema.checkout.detail)
    } catch {
      c = undefined
    }
    if (!c?.id) {
      toast.error('Nepodařilo se ověřit stav pokladny')
      unlockClosing()
      return
    }
    const isAlreadyClosed = !c.currentClosing?.dateOpen || !!c.currentClosing?.dateClosed
    if (isAlreadyClosed) {
      toast.info(`Pokladna ${c.name} už je uzavřená`)
      setSelectedCheckoutId(undefined)
      navigate('/checkout-select')
      return
    }
    const sumDiff = Number(realClosingAmount.toFixed(2)) - Number(checkout.amount.toFixed(2))
    if (sumDiff !== 0) {
      try {
        await createExtraOperation(sumDiff)
      } catch {
        toast.error('Vyrovnávací operace selhala, pokladna nebyla uzavřena')
        unlockClosing()
        return
      }
    }
    updateCheckoutClosing()
  }

  return checkout?.id > 0 ? (
    <div className="w-full h-body p-6 flex flex-col overflow-y-auto max-w-3xl mx-auto">
      <h1 className="text-center font-extrabold text-3xl mb-6 uppercase">Uzavření pokladny</h1>
      <div className="border border-base-300 rounded-md bg-white w-full">
        <div className="flex flex-row justify-center mb-4 border-b border-base-300 py-4 px-4">
          <div className="font-semibold text-base-400 text-2xl flex flex-col items-center">
            <FontAwesomeIcon icon={['far', 'cash-register']} className="text-primary text-2xl mb-2" />
            {checkout.name}
          </div>
        </div>
        <div className="border-b border-base-300">
          <CheckoutTileDetail checkout={checkout} isShowingClosingData={true} />
        </div>
        <div className="p-4">
          <FormInput
            label={'Aktuální stav kasy'}
            type="number"
            value={`${realClosingAmount}`}
            isDisabled={!hasRoleExtraOperation}
            className="flex !flex-row items-center justify-between"
            onChange={val => setRealClosingAmount(Number(val))} />
          <FormTextarea textAreaclassName="w-full" label={'Poznámka'} value={note} onChange={val => setNote(val)} />
        </div>
        <div className="p-4">
          <div>Výčetka přijatých prostředků</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-4">
            {Object.keys(enumeration).map(key => (
              <FormInput key={`enum-row-${key}`} type={'number'} labelClassName="w-1/2" label={`${key} ${currency.symbol}`} value={`${enumeration[key]}`} className="flex !flex-row items-center justify-between" onChange={val => updateEnumeration(Number(key), Number(val))} />
            ))}
          </div>
          <div className="text-right text-base-400 font-bold text-lg">Součet: {formatPrice(enumerationSum, currency.id)}</div>
        </div>
        {checkout?.currentClosing?.paymentSum?.length > 0 && (
          <div>
            <div className="bg-white border-b-1 border-solid border-border-light rounded-md p-6">
              <h3 className="font-semibold text-lg mb-4">Operace</h3>
              <div className="space-y-2">
                {checkout?.currentClosing?.paymentSum.map((ps, i) => (
                  <div key={`payment-sum-${i}`} className="pb-0">
                    <div className="flex justify-between items-center mb-0">
                      <span className="">{ps.payment.name}:</span>
                      <span className="font-bold">{ps.count}x</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="">Částka:</span>
                      <span className="font-bold">{formatPrice(ps.sum, currency.id)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border-b-1 border-solid border-border-light rounded-md p-6">
              <h3 className="font-semibold text-lg mb-4">Vyčíslení DPH</h3>
              <div className="overflow-x-auto">
                <VatAllocationsTable vatAllocations={checkout?.currentClosing?.vatAllocations} currency={currency} />
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-row justify-between p-4">
          {/*<Btn className="btn-primary btn-soft" children={'Změnit pokladnu'} icon={'fa-sync'} onClick={backToCheckoutSelect} />*/}
          <div></div>
          <Btn className="btn-primary" children={'Uzavřít pokladnu'} icon={'fa-download'} isDisabled={isClosing} onClick={closeCheckout} />
        </div>
      </div>
    </div>
  ) : null
}
